import { createContext, useCallback, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

export const FRAME_SCALE = 0.78;
export const CROSSFADE_MS = 1500;

const TransitionContext = createContext(null);

export const useTransition = () => useContext(TransitionContext);

export function TransitionProvider({ children }) {
  // outgoing: { image, slug } | null. When set, the overlay is shown and the scale animation runs.
  const [outgoing, setOutgoing] = useState(null);
  // After navigation, the overlay STAYS visible until the destination page reports its
  // hero image is painted. Then we fade the overlay out (mask any iOS reflow), then unmount.
  const [holdOverlay, setHoldOverlay] = useState(false);
  // Drives a 300ms CSS opacity fade-out on the overlay container so any tiny
  // iOS Safari reflow / snap recompute at handoff is hidden visually rather
  // than landing as an abrupt jump.
  const [fadingOut, setFadingOut] = useState(false);
  const navigate = useNavigate();

  const startOutgoing = useCallback((image, slug) => {
    setOutgoing({ image, slug });
    setHoldOverlay(false);
    setFadingOut(false);
    // Mark transitioning so useVisualViewportCenter's rAF poll loop activates
    // — critical for iOS Safari, where vv events don't fire during URL-bar
    // animation. The attribute is cleared in notifyDestinationReady after
    // the overlay fully unmounts.
    document.documentElement.setAttribute("data-transitioning", "true");
    // setOutgoing / setHoldOverlay / setFadingOut are stable refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAnimationComplete = useCallback(() => {
    if (!outgoing) return;
    // Lock the overlay in its final state for the cross-route hand-off.
    setHoldOverlay(true);
    // CRITICAL: scroll-snap on both pages share the same 100lvh grid. iOS
    // Safari (and only iOS) can preserve the originating scroll position
    // across the SPA route change, which causes the new page's mandatory
    // snap to pull the user onto frame 2. The defense is the overflow:hidden
    // lock — but that lock also hides classic desktop scrollbars, which
    // causes a visible ~15px leftward content shift at the end of the
    // transition on macOS / Windows with "Always show" scrollbars.
    //
    // We detect "needs the iOS-style lock" by measuring whether the platform
    // has a classic (space-taking) scrollbar. If scrollbarWidth > 0 we're on
    // a desktop with classic scrollbars — no iOS bug to defend against, so
    // we skip the lock entirely (just snap reset + scrollTo). If
    // scrollbarWidth === 0 we're on mobile / overlay-scrollbar where the
    // lock is needed AND won't cause a layout reflow because there is no
    // scrollbar to disappear.
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    const html = document.documentElement;
    const body = document.body;
    const scrollbarWidth = window.innerWidth - html.clientWidth;

    html.style.scrollSnapType = "none";
    html.style.scrollBehavior = "auto";

    if (scrollbarWidth === 0) {
      // Mobile / overlay scrollbar: full overflow lock for iOS frame-2 fix.
      html.style.overflow = "hidden";
      body.style.overflow = "hidden";
    }
    // Desktop with classic scrollbar: NO overflow lock → no scrollbar
    // reflow → no leftward jump at the end of the transition.

    window.scrollTo(0, 0);
    html.scrollTop = 0;
    body.scrollTop = 0;
    navigate(`/project/${outgoing.slug}`);
    // `html`, `body`, `scrollbarWidth` are locals scoped to this callback —
    // not React-tracked deps. outgoing/navigate are the only reactive inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outgoing, navigate]);

  const notifyDestinationReady = useCallback(() => {
    // The destination page has decoded & painted its hero image. Sequence:
    //   1. Release the overflow lock IMMEDIATELY so iOS Safari can finish
    //      any pending URL-bar transition, snap recompute, and viewport
    //      reflow. This happens UNDER the still-opaque overlay (opacity 1),
    //      so the user cannot see it.
    //   2. Wait ~250ms — longer than iOS's URL-bar animation (~200-250ms)
    //      and any snap re-engagement — so the underlying page is fully
    //      stable before the user starts seeing through the overlay.
    //   3. Begin a 300ms opacity fade. By now the destination is settled,
    //      so the fade reveals a stable page — no perceived "jump".
    //   4. After the fade, unmount.
    //
    // Why the lock MUST be released first: iOS Safari only animates its URL
    // bar / re-snaps when the body is scrollable. If we hold overflow:hidden
    // through the fade and release only after, the reflow happens AT THE
    // VERY END — perceived as a hard "jump" the moment the overlay is gone.
    const html = document.documentElement;
    const body = document.body;
    html.style.overflow = "";
    body.style.overflow = "";
    window.scrollTo(0, 0);

    // Track if the USER has scrolled since handoff. The defensive
    // `scrollTo(0, 0)` 260 ms below was originally added to absorb late iOS
    // snap recompute — but on desktop users can scroll within that window,
    // and the unconditional re-pin would yank them back from frame 2 to
    // frame 1 (the "jump-then-back" desktop glitch). We only re-pin if the
    // user is still at scrollY === 0.
    let userScrolled = false;
    const onScroll = () => {
      if (window.scrollY > 0) userScrolled = true;
    };
    // Wait one frame so the programmatic scrollTo(0, 0) above doesn't
    // register as a user scroll, then start listening.
    requestAnimationFrame(() => {
      window.addEventListener("scroll", onScroll, { passive: true });
    });

    // Give iOS Safari a generous window to complete its URL-bar animation
    // and any snap recompute under the still-opaque overlay.
    setTimeout(() => {
      window.removeEventListener("scroll", onScroll);
      // Only re-pin if the user hasn't taken control. Without this guard
      // we'd hijack a user who started scrolling during the 260 ms wait.
      if (!userScrolled) {
        window.scrollTo(0, 0);
      }
      setFadingOut(true);
      setTimeout(() => {
        setOutgoing(null);
        setHoldOverlay(false);
        setFadingOut(false);
        // Clear the transitioning flag — useVisualViewportCenter's rAF poll
        // loop will stop on the next frame. Delay slightly past unmount so
        // any final iOS chrome settling is still tracked.
        setTimeout(() => {
          document.documentElement.removeAttribute("data-transitioning");
        }, 200);
      }, 320); // 300ms fade + 20ms buffer
    }, 260); // long enough to outlast iOS Safari URL-bar transition
    // All setters are stable; setTimeout / scrollTo are globals.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <TransitionContext.Provider value={{ startOutgoing, notifyDestinationReady }}>
      {children}

      {outgoing && (
        <div
          data-testid="case-study-transition-overlay"
          className={`fixed left-0 top-0 z-[100] overflow-hidden ${fadingOut ? "pointer-events-none" : "pointer-events-auto"}`}
          style={{
            height: "100lvh",
            // Match the destination page's body-content width exactly so that
            // when the overlay disappears the underlying HeroFrame (which is
            // w-full = clientWidth, i.e. excludes the scrollbar gutter) sits
            // at the same horizontal extent. Without this the overlay would
            // be `w-screen` (= 100vw including scrollbar) on the originating
            // page and the user sees a ~half-scrollbar leftward jump at
            // hand-off. `--sbw` is set globally by useScrollbarGutterVar().
            width: "calc(100vw - var(--sbw, 0px))",
            // Smooth opacity fade-out at handoff (set by notifyDestinationReady)
            // to mask any tiny iOS Safari reflow / snap recompute. The page
            // underneath is released from the overflow lock and given ~260ms
            // to settle BEFORE this fade begins — so by the time the user
            // can see through the overlay, the destination is fully stable.
            opacity: fadingOut ? 0 : 1,
            transition: "opacity 300ms ease",
            willChange: "opacity"
          }}
        >
          <div className="absolute inset-0 bg-paper" />
          {/* Visual-viewport tracker: translates the scaled image by `--vv-y`
              (set by useVisualViewportCenter) so it actively follows the
              visual viewport's center as iOS Safari animates its chrome at
              the end of the route transition. Without this, the image would
              stay at the layout-viewport center (`lvh/2`) while iOS shifts
              the visible area underneath — producing the 40-60 px "jump"
              the user saw the moment the overlay finished fading. */}
          <div
            className="absolute inset-0"
            style={{ transform: "translateY(var(--vv-y, 0px))" }}
          >
            <motion.div
              key="transition-image"
              initial={{ scale: 1 }}
              animate={{ scale: holdOverlay ? FRAME_SCALE : FRAME_SCALE }}
              transition={
                holdOverlay
                  ? { duration: 0 }
                  : { duration: CROSSFADE_MS / 1000, ease: [0.22, 0.61, 0.36, 1] }
              }
              onAnimationComplete={handleAnimationComplete}
              className="absolute inset-0 origin-center overflow-hidden will-change-transform"
            >
              <div className="absolute inset-[-22%]">
                <img src={outgoing.image} alt="" className="w-full h-full object-cover" />
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </TransitionContext.Provider>
  );
}
